import {FormikErrors} from 'formik';
import {set} from 'lodash';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {transferSourceType} from './builderTransferFields';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';

export const transferValidator = (
  values: builderFieldsType,
  errors: FormikErrors<builderFieldsType>
): FormikErrors<builderFieldsType> => {
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const transferValues = values.transfer;
  if (!transferValues) {
    errors.transfer = 'Please fill in transfer fields';
    return errors;
  }
  if (
    !transferValues.transferSources ||
    Object.values(transferValues.transferSources).length === 0
  ) {
    set(errors, 'transfer.transferSources', 'Please select at least one account');
  } else {
    Object.values(transferValues.transferSources).forEach((source) => {
      // shell-plan-only validation (new sources): plan number, plan type, provider, value,
      // contact, and the isDataGather/isAuthorityHeld pair.
      if (source.isShellPlan || source.id.includes('shell')) {
        transferShellValidator(source, values.isSppDa, values.mainPlanType, errors);
      }
      transferAmountValidator(source, errors);
      // initial-fee validation — runs for every source regardless of shell/existing
      if (!values.isLisa) {
        if (values.isSppDa) {
          if (!source.legacyInitialFee?.feeType) {
            set(
              errors,
              `transfer.transferSources.${source.id}.legacyInitialFee.feeType`,
              'Required'
            );
          }
          if (
            source.legacyInitialFee?.feeType === '%' &&
            source.legacyInitialFee.percentageFee === undefined
          ) {
            set(
              errors,
              `transfer.transferSources.${source.id}.legacyInitialFee.percentageFee`,
              'Required'
            );
          }
          if (
            source.legacyInitialFee?.feeType === 'GBP' &&
            source.legacyInitialFee.fixedFee === undefined
          ) {
            set(
              errors,
              `transfer.transferSources.${source.id}.legacyInitialFee.fixedFee`,
              'Required'
            );
          }
        } else if (!source.initialFee) {
          set(errors, `transfer.transferSources.${source.id}.initialFee`, 'Required');
        }
      }
    });
  }

  if (!transferValues.destinationValues?.existingPotId) {
    set(errors, 'transfer.destinationValues.existingPotId', 'Please select an account');
  }
  console.log({values, errors});
  return errors;
};

const transferShellValidator = (
  source: transferSourceType,
  isSppDa: boolean,
  mainPlanType: builderFieldsType['mainPlanType'],
  errors: FormikErrors<builderFieldsType>
): FormikErrors<builderFieldsType> => {
  const key = `transfer.transferSources.${source.id}`;
  // DBO-1418: pension transfers only; zero is a valid answer, blank is not
  if (mainPlanType === 'pension') {
    const {drawdownAmount} = source;
    if (
      drawdownAmount === undefined ||
      drawdownAmount === null ||
      drawdownAmount === ''
    ) {
      set(errors, `${key}.drawdownAmount`, 'Required');
    } else if (
      source.planValue &&
      getCurrencyFloat(drawdownAmount) > getCurrencyFloat(source.planValue)
    ) {
      set(
        errors,
        `${key}.drawdownAmount`,
        'Amount in drawdown cannot be greater than the plan value'
      );
    }
  }
  if (!source.accountNumber) {
    set(errors, `${key}.accountNumber`, 'Required');
  }
  if (!source.planProvider) {
    set(errors, `${key}.planProvider`, 'Required');
  }
  if (!source.planType) {
    set(errors, `${key}.planType`, 'Required');
  }
  if (!source.planValue) {
    set(errors, `${key}.planValue`, 'Required');
  }
  if (!source.full && !source.singleClientAmount) {
    set(errors, `${key}.singleClientAmount`, 'Required');
  }
  if (!source.contactId) {
    set(errors, `${key}.contactId`, 'Required');
  }
  // isDataGather / isAuthorityHeld — tri-state string ('yes' | 'no' | '')
  // Required on shell plans only (existing sources read these from the pot).
  if (!isSppDa) {
    if (source.isDataGather) {
      if (source.isDataGather === 'yes' && !source.isAuthorityHeld) {
        set(errors, `${key}.isAuthorityHeld`, 'Required');
      }
    } else {
      set(errors, `${key}.isDataGather`, 'Required');
    }
  }
  return errors;
};

const transferAmountValidator = (
  source: transferSourceType,
  errors: FormikErrors<builderFieldsType>
): FormikErrors<builderFieldsType> => {
  if (source.full) {
    return errors;
  }
  /**
   * Not using getCurrencyFloat(source.planValue) because
   * sometimes it might read less than what's actually in it due to the 24 hour delay
   * in how oneview can ingest the data, which could cause problems down the line
   */
  const planValue = getCurrencyFloat(source.maxAmount);
  if (!planValue) {
    return errors;
  }

  const singleClientAmount = getCurrencyFloat(source.singleClientAmount);

  if (singleClientAmount > planValue) {
    set(
      errors,
      `transfer.transferSources.${source.id}.singleClientAmount`,
      'Amount cannot be greater than the plan value'
    );
  } else if (singleClientAmount <= 0) {
    set(
      errors,
      `transfer.transferSources.${source.id}.singleClientAmount`,
      'Amount must be larger than zero'
    );
  }
  return errors;
};
